import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_URL, API_URL_UPLOADS } from "../components/config";

const UserDetailsSidebar = ({ customerId }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [userType, setUserType] = useState(""); // "customer" | "driver" | "internal"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserDetails = async () => {
    setLoading(true);
    setError(null);
    setUserType("");
    setUserDetails(null);

    try {
      let response;
      // 1. Try Customer
      if (customerId) {
        response = await axios.get(`${API_URL}/customerdetails/${customerId}`);
        if (response.data && response.data.length > 0) {
          setUserDetails(response.data[0]);
          setUserType("customer");
          setLoading(false);
          return;
        }
      }
      // 2. Try Driver
      response = await axios.get(`${API_URL}/driver/${customerId}`);
      if (response.data && response.data.length > 0) {
        setUserDetails(response.data[0]);
        setUserType("driver");
        setLoading(false);
        return;
      }
      // 3. Try Internal User
      response = await axios.get(`${API_URL}/users/${customerId}`);
      if (response.data && response.data.length > 0) {
        setUserDetails(response.data[0]);
        setUserType("internal");
        setLoading(false);
        return;
      }

      setError("No user details found for the provided ID.");
    } catch (err) {
      setError("Failed to fetch user details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
    // eslint-disable-next-line
  }, [customerId]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-40 text-[#002966] font-semibold">
        Loading...
      </div>
    );
  if (error)
    return (
      <div className="text-red-500 p-4 bg-red-50 rounded-lg text-center">
        {error}
      </div>
    );

  return (
    <div className="bg-[#002966] p-0 md:p-4 rounded-xl shadow-2xl min-w-[340px] max-w-full">
      <div className="bg-[#002966] p-5 pb-2 text-white shadow-lg rounded-t-xl flex flex-col gap-2">
        <h2 className="text-xl font-bold tracking-wide">User Details</h2>
        {userType === "internal" && (
          <span className="inline-block bg-white text-[#002966] px-3 py-1 rounded-full font-semibold text-xs shadow-md w-fit">
            Internal User
          </span>
        )}
        {userType === "driver" && (
          <span className="inline-block bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-semibold text-xs shadow-md w-fit">
            Driver
          </span>
        )}
        {userType === "customer" && (
          <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold text-xs shadow-md w-fit">
            Customer
          </span>
        )}
      </div>
      <div className="bg-white rounded-b-xl shadow-lg p-6 mb-2">
        {userDetails ? (
          <div>
            {/* Avatar / Profile Pic */}
            <div className="flex items-center mb-6">
              {userType !== "internal" && userDetails.profilePic && (
                <img
                  src={`${API_URL_UPLOADS}/${userDetails.profilePic}`}
                  alt={`${userDetails.name || userDetails.username} ${userDetails.surname || ""}`}
                  className="w-20 h-20 rounded-full object-cover border-4 border-[#002966] shadow-xl"
                />
              )}
              {userType === "internal" && (
                <div className="w-20 h-20 rounded-full bg-[#e9f1fb] flex items-center justify-center border-4 border-[#002966] shadow-xl">
                  <span className="text-3xl font-bold text-[#002966] select-none">
                    {userDetails.username
                      ? userDetails.username
                          .split(" ")
                          .map((s) => s[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase()
                      : "U"}
                  </span>
                </div>
              )}
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-[#002966]">
                  {userDetails.name || userDetails.username || "N/A"}{" "}
                  {userDetails.surname || ""}
                </h3>
                <p className="text-gray-600">{userDetails.email || "N/A"}</p>
                {userType !== "internal" && (
                  <p className="text-gray-600">{userDetails.phone || "N/A"}</p>
                )}
                {userType === "internal" && (
                  <p className="text-[#002966] font-medium text-xs">
                    <span className="bg-[#f0f4fa] px-2 py-1 rounded-lg">
                      {userDetails.role ||
                        userDetails.category ||
                        "No role specified"}
                    </span>
                  </p>
                )}
              </div>
            </div>

            {userType === "driver" && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-[#002966]">
                    Vehicle Information
                  </h4>
                  <p>
                    <strong>Vehicle:</strong> {userDetails.make || "N/A"}{" "}
                    {userDetails.model || "N/A"}
                  </p>
                  <p>
                    <strong>Plate:</strong> {userDetails.plate || "N/A"}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {userDetails.number_plate_image && (
                    <div className="relative group">
                      <img
                        src={`${API_URL_UPLOADS}/${userDetails.number_plate_image}`}
                        alt="Number Plate"
                        className="w-full h-32 object-cover rounded-lg shadow-md transition-transform duration-300 group-hover:scale-105"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs py-1 px-2 rounded-b-lg">
                        Number Plate
                      </div>
                    </div>
                  )}
                  {userDetails.driver_license_image && (
                    <div className="relative group">
                      <img
                        src={`${API_URL_UPLOADS}/${userDetails.driver_license_image}`}
                        alt="Driver License"
                        className="w-full h-32 object-cover rounded-lg shadow-md transition-transform duration-300 group-hover:scale-105"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs py-1 px-2 rounded-b-lg">
                        Driver License
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Internal User Details */}
            {userType === "internal" && (
              <div className="mt-3 space-y-2">
                <p>
                  <strong>User ID:</strong> {userDetails.userid}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  <span
                    className={`px-2 py-1 rounded ${
                      userDetails.status === "active"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {userDetails.status}
                  </span>
                </p>
                {userDetails.role && (
                  <p>
                    <strong>Role:</strong>{" "}
                    <span className="bg-[#e9f1fb] text-[#002966] px-2 py-1 rounded">
                      {userDetails.role}
                    </span>
                  </p>
                )}
                {userDetails.last_logged_in && (
                  <p>
                    <strong>Last Login:</strong>{" "}
                    {new Date(userDetails.last_logged_in).toLocaleString()}
                  </p>
                )}
                {userDetails.signed_up_on && (
                  <p>
                    <strong>Signed Up:</strong>{" "}
                    {new Date(userDetails.signed_up_on).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}

            {/* Customer/Driver additional info */}
            {(userType === "customer" || userType === "driver") && (
              <div className="mt-4 space-y-2">
                <p>
                  <strong>Address:</strong> {userDetails.address || "N/A"}
                </p>
                <p>
                  <strong>Rating:</strong>{" "}
                  <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                    {userDetails.rating || "N/A"}
                  </span>
                </p>
                <p>
                  <strong>Membership Status:</strong>{" "}
                  {userDetails.membershipstatus || "N/A"}
                </p>
              </div>
            )}
          </div>
        ) : (
          <p>No user details available.</p>
        )}
      </div>
    </div>
  );
};

export default UserDetailsSidebar;